import { dateFormat } from '@/helpers/dateFormat';
import { toNumber } from '@/helpers/toNumber';
import { IdNomeDescDTO, IdNomeDTO, ResultPagDTO } from '@/interfaces/base-dto';
import { AlterDadosBasicPrescrParmDTO, AprovacaoAgendadaMultiParmDTO, BolsaCompartMinMaxDTO, BolsaMulticompartPrescricaoDTO, EnteralPrescricaoListDTO, PrescricaoOpcoesProdutosParmDTO, PrescricaoAlterParmDTO, PrescricaoFiltroParmDTO, PrescricaoParmDTO, PrescricaoResumoParmDTO, TranscricaoParmDTO } from '@/interfaces/prescription';
import { ConversaoMedidasDTO, MedidasProdutosEnteralCalcParm, MedidasProdutosEnteralResult, UnidadesMedidasProdutosPacienteCalcParm } from '@/interfaces/product';
import { TerapiaUsoDTO, UsoTerapiaSimplifFiltro } from '@/interfaces/therapy';
import { TranscricaoOcrParm, TranscricaoOcrResult, TranscricaoOcrValidValorParm } from '@/interfaces/transcricao-ocr';
import { AprovacaoTranscricaoParmDTO, ITranscription, TranscricaoFiltroParmDTO } from '@/interfaces/transcription';
import { apiClient } from '@/services/api';
import { API_URL, ENDPOINTS } from '@/settings/api-settings';
import { WAITING_PHARMACEUTICAL_APPROVE } from '@/settings/consts/prescription-status';
import { from, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

const _associateProductPrescription = (prescriptions, products) => {
  prescriptions.data = prescriptions.data.map((prescription) => {
    prescription.produtos = prescription.produtos.map((product) => {
      const filterProduct = products.find(
        (item) => item.id === product.idProduto,
      );

      product.descricao = !!filterProduct ? filterProduct.descricao : '';

      return product;
    });
    return prescription;
  });
  return of(prescriptions);
};

async function testPrescription(payload: any) {
  const response = await apiClient.post('/prescricao', payload);
  return response.data;
}

function calculaUnidadeValores(
  idProduto,
  valor,
  idMedida,
  peso,
  infusao,
  volume,
) {
  return apiClient
    .get(`/produtos/${idProduto}/medidas`, {
      valor,
      unidadeMedida: idMedida,
      peso,
      tempoInfusao: infusao,
      volumeTotal: volume,
    })
    .then((r) => {
      return r;
    });
}
// Observable based

function resumeGenerate(payload: any): Observable<any> {
  return from(
    apiClient.post(ENDPOINTS.PRESCRIPTION_RESUME, {
      prescricao: payload.prescription,
      produtos: payload.products,
      peso: payload.weight,
      tempoDeInfusao: payload.infusionTime,
      volumeFinal: toNumber(payload.volume),
      divisaoBolsas: payload.division,
    }),
  ).pipe(map((r: any) => r.data || {}));
}

export function resumeGenerateV2(payload: PrescricaoResumoParmDTO): Observable<any> {
  return from(
    apiClient.post(ENDPOINTS.PRESCRIPTION_RESUME_V2, payload),
  ).pipe(map((r: any) => r.data || {}));
}

export function getPrescriptionProductsOptions(parm: PrescricaoOpcoesProdutosParmDTO): Observable<any[]> {
  return from(
    apiClient.get(ENDPOINTS.PRESCRIPTION_PRODUCTS_OPTIONS, parm),
  ).pipe(map((r: any) => r.data || []));
}

export function listPrescriptionPatientsCombo(clientUnitId: number, status: boolean): Observable<any[]> {
  return from(apiClient.get(ENDPOINTS.PRESCRIPTION_PATIENTS_COMBO.replaceAll('{idUnidade}', clientUnitId.toString()), { status: status })).pipe(
    map((r: any) => r.data || []),
  );
}

export function getClientPatientDetails(idPatient, idClient): Observable<any> {
  return from(apiClient.get(`/clientes/${idClient}/pacientes/${idPatient}`)).pipe(
    map((r: any) => r.data || []),
  );
}

function validatePrescription(payload: any): Observable<any> {
  return from(apiClient.post(ENDPOINTS.PRESCRIPTION_VALIDATE, payload)).pipe(
    map((r: any) => r.data || []),
  );
}

export function validatePrescriptionV2(payload: PrescricaoParmDTO): Observable<any> {
  return from(apiClient.post(`${ENDPOINTS.PRESCRIPTION_VALIDATION_V2}`, payload)).pipe(
    map((r: any) => r.data || []),
  );
}

export function getValidationPrescriptionV2(id: Number): Observable<any> {
  return from(apiClient.get(`${ENDPOINTS.PRESCRIPTION_VALIDATION_V2}/${id}`)).pipe(
    map((r: any) => r.data || []),
  );
}

export function listStatus(): Observable<any> {
  return from(apiClient.get(ENDPOINTS.PRESCRIPTION_STATUS)).pipe(
    map((r: any) => r.data || []),
  );
}

export function listPrescriptions(filter: PrescricaoFiltroParmDTO): Observable<ResultPagDTO<any>> {
  return from(apiClient.get(ENDPOINTS.PRESCRIPTION_V2, filter)).pipe(
    map((r: any) => r.data || []),
  );
}

export function listTranscriptions(filter: TranscricaoFiltroParmDTO): Observable<ResultPagDTO<any>> {
  return from(apiClient.get(ENDPOINTS.TRANSCRIPTION, filter)).pipe(
    map(({ data }) => {
      const transcricoes = data.content.map((item: ITranscription) => {
        item.prescricoes = item.prescricoes.map((prescription) => {
          if (prescription.dataPrescricao) {
            prescription.dataPrescricaoTime = dateFormat(
              prescription.dataPrescricao[0],
              'UNIX',
            );
            prescription.dataPrescricao = dateFormat(prescription.dataPrescricao[0]);
          }
          prescription.dataCriacao = dateFormat(prescription.dataCriacao);
          return prescription;
        });

        if (item.dataInclusao) {
          item.dataInclusao = dateFormat(item.dataInclusao);
        }
        const prescriptionWaitingApprove = item.prescricoes.find(
          (prescription) =>
            prescription.statusPrescricao ===
            WAITING_PHARMACEUTICAL_APPROVE.key,
        );

        let prescriptionReference;
        if (!!item.prescricaoAprovada) {
          item.prescricaoAprovada.dataPrescricaoTime = dateFormat(
            item.prescricaoAprovada.dataPrescricao[0],
            'UNIX',
          );
          item.prescricaoAprovada.dataPrescricao = dateFormat(
            item.prescricaoAprovada.dataPrescricao[0]
          );
          item.prescricaoAprovada.dataCriacao = dateFormat(
            item.prescricaoAprovada.dataCriacao,
          );
          prescriptionReference = item.prescricaoAprovada;
        } else if (!!item.prescricoes.length) {
          prescriptionReference = item.prescricoes[0];
        }

        return {
          ...item,
          prescricaoAguardandoAprovacao: prescriptionWaitingApprove,
          prescricaoReferencia: prescriptionReference,
        };
      });
      data.content = transcricoes;
      return data;
    }),
  );
}

export function listPrescriptionsModelsCombo(idPatient: number, idClientUnit: number): Observable<any> {
  return from(apiClient.get(`${ENDPOINTS.PRESCRIPTION_MODELS_COMBO}`, {idUnidade: idClientUnit, idPaciente: idPatient})).pipe(
    map((r: any) => r.data),
  );
}

function getPrescription(id: string): Observable<any> {
  return from(apiClient.get(`${ENDPOINTS.PRESCRIPTION}/${id}?lean=true`)).pipe(
    map((r: any) => r.data),
  );
}

export function getPrescriptionByUuid(uuid: string): Observable<any> {
  return from(apiClient.get(`${ENDPOINTS.PRESCRIPTION_UUID}/${uuid}`)).pipe(
    map((r: any) => r.data),
  );
}

export function getPrescriptionProducts(id: string): Observable<any> {
  let endpoint = `${ENDPOINTS.PRESCRIPTION_PRODUCTS}`.replace('{id}', id);
  return from(apiClient.get(endpoint)).pipe(
    map((r: any) => r.data),
  );
}

function savePrescription(payload: any): Observable<any> {
  return from(apiClient.post(ENDPOINTS.PRESCRIPTION, payload)).pipe(
    map((r: any) => r.data),
  );
}

export function savePrescriptionV2(payload: PrescricaoParmDTO): Observable<any> {
  return from(apiClient.post(`${ENDPOINTS.PRESCRIPTION_V2}/async`, payload)).pipe(
    map((r: any) => r.data),
  );
}

export function alterPrescriptionV2(payload: PrescricaoAlterParmDTO): Observable<any> {
  return from(apiClient.put(`${ENDPOINTS.PRESCRIPTION_V2}/async`, payload)).pipe(
    map((r: any) => r.data),
  );
}

function getTranscription(id: string): Observable<any> {
  return from(apiClient.get(`${ENDPOINTS.TRANSCRIPTION}/${id}`)).pipe(
    map((r: any) => {
      r.data.prescricoes = r.data.prescricoes.map((prescription) => {
        prescription.dataPrescricaoTime = dateFormat(
          prescription.dataPrescricao,
          'UNIX',
        );
        return prescription;
      });
      return !!r.data ? r.data : [];
    }),
  );
}

function getPrescriptionTranscription(id: string): Observable<any> {
  return from(
    apiClient.get(`${ENDPOINTS.TRANSCRIPTION_PRESCRIPTION}/${id}?lean=true`),
  ).pipe(map((r: any) => r.data));
}

function getTranscriptionToCompare(id: string): Observable<ITranscription> {
  return from(apiClient.get(`${ENDPOINTS.TRANSCRIPTION}/${id}?lean=true`)).pipe(
    map((r: any) => r.data),
  );
}

export function saveTranscription(parm: TranscricaoParmDTO, file: any): Observable<any> {
  return from(apiClient.postMultipartFormData(ENDPOINTS.TRANSCRIPTION, {parm, file})).pipe(
    map((r: any) => r.data),
  );
}

function unsetTranscriptionProgress(idTranscription: string): Observable<any> {
  return from(
    apiClient.put(`${ENDPOINTS.TRANSCRIPTION_DISCART}/${idTranscription}`, {}),
  ).pipe(map((r: any) => r.data));
}

function setTranscriptionProgress(idTranscription: string): Observable<any> {
  return from(
    apiClient.put(`${ENDPOINTS.TRANSCRIPTION_INIT}/${idTranscription}`, {}),
  ).pipe(map((r: any) => r.data));
}

function cancelTranscription(id: string): Observable<any> {
  return from(
    apiClient.put(`${ENDPOINTS.TRANSCRIPTION_CANCEL}/${id}`, {}),
  ).pipe(map((r: any) => r.data));
}

function clientAprovation(id: string): Observable<any> {
  return from(
    apiClient.put(`${ENDPOINTS.PRESCRIPTION_CLIENT_APROVATION}/${id}`, {}),
  ).pipe(map((r: any) => r.data));
}

export function validatePreapproval(id: string): Observable<any> {
  const endpoint = `${ENDPOINTS.PRESCRIPTION_PREAPPROVAL_VALIDATION}`.replace('{id}', id);
  return from(
    apiClient.get(endpoint),
  ).pipe(map((r: any) => r.data));
}

function emedeAprovation(id: string, justification?: string, newDeliveryDate?: string): Observable<any> {
  return from(
    apiClient.put(`${ENDPOINTS.PRESCRIPTION_EMEDE_APROVATION}`, { idPrescricao: id, justifProducaoAdiantada: justification, novaDataEntrega: newDeliveryDate}),
  ).pipe(map((r: any) => r.data));
}

export function getAprovationScheduleDetails(id: string): Observable<any> {
  return from(apiClient.get(`${ENDPOINTS.PRESCRIPTION_EMEDE_APROVATION_SCHEDULE_DETAILS}/${id}`)).pipe(
    map((r: any) => r.data),
  );
}

export function emedeAprovationSchedule(ids: number[], hour: string): Observable<any> {
  return from(
    apiClient.put(`${ENDPOINTS.PRESCRIPTION_EMEDE_APROVATION_SCHEDULE}`, { idsPrescricao: ids, horario: hour }),
  ).pipe(map((r: any) => r.data));
}

export function emedeAprovationScheduleMulti(parms: AprovacaoAgendadaMultiParmDTO): Observable<any> {
  return from(
    apiClient.put(`${ENDPOINTS.PRESCRIPTION_EMEDE_APROVATION_SCHEDULE_MULTI}`, parms),
  ).pipe(map((r: any) => r.data));
}

function transcriptionPrescriptionAprovation(parm: AprovacaoTranscricaoParmDTO): Observable<any> {
  return from(
    apiClient.put(`${ENDPOINTS.TRANSCRIPTION_APROVATION}`,parm),
  ).pipe(map((r: any) => r.data));
}

function cancelPrescription(id: string, reasonId: string): Observable<any> {
  return from(
    apiClient.put(`${ENDPOINTS.PRESCRIPTION_CANCEL}/${id}`, {
      idMotivo: reasonId,
    }),
  ).pipe(map((r: any) => r.data));
}

export function getDeletePrescriptionGroup(id: string): Observable<IdNomeDTO[]> {
  const endpoint = `${ENDPOINTS.PRESCRIPTION_DELETE_GROUP}`.replace('{id}', id);
  return from(apiClient.get(endpoint)).pipe(
    map((r: any) => r.data),
  );
}

export class PrescricaoExclusaoParmDTO {
  idsPrescricao: number[];
  idMotivo: number;
}

function deletePrescription(parm: PrescricaoExclusaoParmDTO): Observable<any> {
  return from(
    apiClient.delete(`${ENDPOINTS.PRESCRIPTION}`, parm),
  ).pipe(map((r: any) => r.data));
}

export function getAlterPrescriptionGroup(id: string): Observable<IdNomeDTO[]> {
  const endpoint = `${ENDPOINTS.PRESCRIPTION_ALTER_GROUP}`.replace('{id}', id);
  return from(apiClient.get(endpoint)).pipe(
    map((r: any) => r.data),
  );
}

export function getLockPrescriptionGroup(id: string): Observable<IdNomeDTO[]> {
  const endpoint = `${ENDPOINTS.PRESCRIPTION_LOCK_GROUP}`.replace('{id}', id);
  return from(apiClient.get(endpoint)).pipe(
    map((r: any) => r.data),
  );
}

export function getUnlockPrescriptionGroup(id: string): Observable<IdNomeDTO[]> {
  const endpoint = `${ENDPOINTS.PRESCRIPTION_UNLOCK_GROUP}`.replace('{id}', id);
  return from(apiClient.get(endpoint)).pipe(
    map((r: any) => r.data),
  );
}

export class PrescricaoBloqueioParmDTO {
  idsPrescricao: number[];
  idMotivo: number;
  descricao: string;
}

export function lockPrescriptions(parm: PrescricaoBloqueioParmDTO): Observable<any> {
  return from(
    apiClient.put(`${ENDPOINTS.PRESCRIPTION_LOCK}`, parm),
  ).pipe(map((r: any) => r.data));
}

export class PrescricaoDesbloqueioParmDTO {
  idsPrescricao: number[];
}

export function unlockPrescriptions(parm: PrescricaoDesbloqueioParmDTO): Observable<any> {
  return from(
    apiClient.put(`${ENDPOINTS.PRESCRIPTION_UNLOCK}`, parm),
  ).pipe(map((r: any) => r.data));
}

function getHealthInsurance(): Observable<any> {
  return from(apiClient.get(ENDPOINTS.HEALTH_INSURANCE)).pipe(
    map((r: any) => (!!r.data ? r.data : [])),
  );
}

function getHealthInsuranceByClient(unitClientId: string): Observable<any> {
  return from(
    apiClient.get(`${ENDPOINTS.HEALTH_INSURANCE_BY_CLIENT}/${unitClientId}`),
  ).pipe(map((r: any) => (!!r.data ? r.data : [])));
}

function getAddressesPatient(
  patientId: string,
  clientUnitId: string,
  multicomp: boolean,
): Observable<any[]> {
  return from(
    apiClient.get(
      `${ENDPOINTS.PRESCRIPTION_ADDRESS}/${patientId}/${clientUnitId}/${multicomp}/enderecos`,
    ),
  ).pipe(map((r: any) => (!!r && !!r.data ? r.data : [])));
}

export function listDeliveryDates(idClientUnit: number, idDoctor: number, idAddress: number, multiCompBag: boolean = false): Observable<string[]> {
  return from(apiClient.get(ENDPOINTS.PRESCRIPTION_DELIVERY_DATES, { idUnidade: idClientUnit, idMedico: idDoctor, bolsaMulti: multiCompBag, idEndereco: idAddress })).pipe(
    map((r: any) => (!!r ? r.data : {})),
  );
}

function unitsConversion(productId: string, payload: any): Observable<any> {
  return from(apiClient.post(`produtos/v2/${productId}/medidas`, payload)).pipe(
    map((r: any) => (!!r ? r.data : {})),
  );
}

export function calcMultiUnitsConversion(payload: UnidadesMedidasProdutosPacienteCalcParm): Observable<ConversaoMedidasDTO[]> {
  return from(apiClient.post(ENDPOINTS.PRODUCT_MULTI_UNITS_CONVERSION, payload)).pipe(map((r: any) => (!!r ? r.data : {})));
}

export function calcMultiUnitsEnteralConversion(payload: MedidasProdutosEnteralCalcParm): Observable<MedidasProdutosEnteralResult> {
  return from(apiClient.post(ENDPOINTS.PRODUCT_MULTI_UNITS_ENTERAL_CONVERSION, payload)).pipe(map((r: any) => (!!r ? r.data : {})));
}



function downloadPDF(id: string): Observable<any> {
  return from(
    apiClient.post(ENDPOINTS.PRESCRIPTION_REPORT, {
      prescricaoId: id,
    }),
  ).pipe(
    map((response: any) => {
      if (!response) {
        return null;
      }
      return `${API_URL}${ENDPOINTS.PRESCRIPTION_REPORT_TOKEN}/${response.data.token}`;
    }),
  );
}

function getHistoricoPrescricao(idPrescricao: number): Observable<any> {
  return from(apiClient.get(`${ENDPOINTS.PRESCRIPTION}/${idPrescricao}/historico`)).pipe(
    map((r: any) => r.data || []),
  );
}

export function getPrescriptionTemplate(id: string): Observable<any> {
  return from(apiClient.get(`${ENDPOINTS.PRESCRIPTION_TEMPLATES}/${id}`)).pipe(
    map((r: any) => r.data),
  );
}

export function getEnteralsAvailable(unitId: Number, pacientId: Number, therapyId: Number, doctorId: Number): Observable<EnteralPrescricaoListDTO> {
  return from(apiClient.get(`${ENDPOINTS.PRESCRIPTION_ENTERALS}`, { idUnidade: unitId, idPaciente: pacientId, idTerapia: therapyId, idMedico: doctorId })).pipe(
    map((r: any) => r.data),);
}

export function getMinMaxBolsasMulticompart(unitId: Number): Observable<BolsaCompartMinMaxDTO> {
  return from(apiClient.get(`${ENDPOINTS.PRESCRIPTION}/unidade/${unitId}/bolsasmulticompart/min-max`)).pipe(
    map((r: any) => r.data),);
}

export function getBolsasMulticompartDetalhe(idPrescricao: number): Observable<BolsaMulticompartPrescricaoDTO[]> {
  return from(apiClient.get(`${ENDPOINTS.PRESCRIPTION}/${idPrescricao}/bolsasmulticompart`)).pipe(
    map((r: any) => r.data || []),
  );
}

function getTherapiesPrescription(unitId: Number, prescriptionType: String): Observable<IdNomeDTO[]> {
  return from(
    apiClient.get(ENDPOINTS.PRESCRIPTION_THERAPIES, { idUnidade: unitId, tipoPrescricao: prescriptionType }),
  ).pipe(map((r: any) => (!!r ? r.data : {})));
}

function getTherapyUsePrescription(therapyId: Number, unitId: Number, doctorId: Number, pacientId: Number, weight: Number, tnDays: string): Observable<TerapiaUsoDTO> {
  return from(
    apiClient.get(`${ENDPOINTS.PRESCRIPTION_THERAPIES}/${therapyId}`, { idUnidade: unitId, idMedico: doctorId, idPaciente: pacientId, peso: weight, diasTN: tnDays }),
  ).pipe(map((r: any) => (!!r ? r.data : {})));
}

export function isSimpleTherapyPrescriptionAvailable(pacientId: Number, clientUnitId: Number): Observable<boolean> {
  return from(
    apiClient.get(ENDPOINTS.PRESCRIPTION_SIMPLE_THERAPY_AVAILABLE, { idPaciente: pacientId, idUnidade: clientUnitId }),
  ).pipe(map((r: any) => (!!r ? r.data : {})));
}

export function listSimpleTherapyPrescription(filter: UsoTerapiaSimplifFiltro): Observable<IdNomeDescDTO> {
  return from(
    apiClient.get(ENDPOINTS.PRESCRIPTION_SIMPLE_THERAPY, filter),
  ).pipe(map((r: any) => (!!r ? r.data : {})));
}

export function getPrescriptionTypes(unitId: Number, doctorId: Number): Observable<IdNomeDTO[]> {
  return from(
    apiClient.get(ENDPOINTS.PRESCRIPTION_TYPES, { idUnidade: unitId, idMedico: doctorId }),
  ).pipe(map((r: any) => (!!r ? r.data : {})));
}

export function execTranscriptionOcr(payload: any): Observable<TranscricaoOcrResult> {
  return from(apiClient.postMultipartFormData(`${ENDPOINTS.TRANSCRIPTION_OCR}/`, payload)).pipe(map((r: any) => r.data));
}

export function execTranscriptionConferOcr(id: number): Observable<TranscricaoOcrResult> {
  return from(apiClient.postMultipartFormData(ENDPOINTS.TRANSCRIPTION_OCR_CONFER.replaceAll('{id}', id.toString()) )).pipe(map((r: any) => r.data));
}

export function continueTranscriptionOcr(payload: TranscricaoOcrParm): Observable<TranscricaoOcrResult> {
  return from(apiClient.put(`${ENDPOINTS.TRANSCRIPTION_OCR}/`, payload)).pipe(map((r: any) => r.data));
}

export function valideTranscriptionOcrField(payload: TranscricaoOcrValidValorParm): Observable<any> {
  return from(apiClient.post(`${ENDPOINTS.TRANSCRIPTION_OCR}/validacao`, payload)).pipe(map((r: any) => r.data));
}

export function getClientUnitPrescription(unitId: Number): Observable<any> {
  return from(
    apiClient.get(`${ENDPOINTS.PRESCRIPTION_CLIENT_UNITS}/${unitId}`,),
  ).pipe(map((r: any) => (!!r ? r.data : {})));
}

export function updateBasicPrescriptionInfos(parm: AlterDadosBasicPrescrParmDTO): Observable<any> {
  return from(
    apiClient.put(`${ENDPOINTS.PRESCRIPTION}`, parm),
  ).pipe(map((r: any) => (!!r ? r.data : {})));
}

export {
  calculaUnidadeValores, cancelPrescription, cancelTranscription,
  clientAprovation, deletePrescription, downloadPDF, emedeAprovation, getAddressesPatient, getHealthInsurance,
  getHealthInsuranceByClient, getHistoricoPrescricao, getPrescription, getPrescriptionTranscription, getTherapiesPrescription,
  getTherapyUsePrescription, getTranscription, getTranscriptionToCompare, resumeGenerate, savePrescription, setTranscriptionProgress, testPrescription, transcriptionPrescriptionAprovation, unitsConversion, unsetTranscriptionProgress, validatePrescription
};

